import { useTranslation } from 'react-i18next';
import {
  InfiosChip,
  InfiosContainer,
  InfiosFormatNumber,
  InfiosTable,
  InfiosTableCell,
  InfiosTableColumn,
  InfiosTableHeaderCell,
  InfiosTextBlock,
} from '@infios/uikit/react';
import type { OrderShipment, OrderSummaryData, OrderSummaryLineItem } from '../../interfaces/chat';
import { useReferenceAction } from '../../context/ReferenceActionContext';
import { FormattedDateTime } from '../FormattedDateTime';
import { getStatusChipColor } from './statusChipColor';
import { getStatusLabel } from './statusLabel';
import styles from './ContentBlocks.module.css';

interface OrderSummaryBlockProps {
  data: OrderSummaryData;
}

interface LineRow extends OrderSummaryLineItem {
  id: string;
}

/**
 * The raw order_summary response has no per-line delivery date — only
 * shipment-level `estimatedDeliveryDate`. Resolve via a matching
 * `shipmentId` when the line carries one; otherwise, when the order has
 * exactly one shipment, that shipment's date applies to every line (the
 * common single-shipment case) — with more than one shipment and no
 * `shipmentId` link, there's no reliable line-to-shipment mapping.
 */
function resolveDeliveryDate(line: OrderSummaryLineItem, shipments: OrderShipment[]): string | undefined {
  if (line.shipmentId) {
    return shipments.find((s) => s.shipmentId === line.shipmentId)?.estimatedDeliveryDate;
  }
  return shipments.length === 1 ? shipments[0].estimatedDeliveryDate : undefined;
}

export function OrderSummaryBlock({ data }: OrderSummaryBlockProps) {
  const { t } = useTranslation();
  const onReference = useReferenceAction();
  if (!data.orderNumber && !data.status) return null;

  const shipTo = data.shipTo?.name || [data.shipTo?.city, data.shipTo?.state].filter(Boolean).join(', ');
  const lineItems = Array.isArray(data.lineItems) ? data.lineItems : [];
  const shipments = Array.isArray(data.shipments) ? data.shipments : [];
  const orderTotal = data.totals?.orderTotal;
  // The order response gives us either an internal orderId or just the
  // customer-facing orderNumber — the host can resolve either as a deep-link id.
  const orderRefId = data.orderId || data.orderNumber;

  const lineRows: LineRow[] = lineItems.map((line, idx) => ({
    ...line,
    id: line.lineItemId ?? String(idx),
  }));

  const renderItem = (row: LineRow) => row.skuId ?? row.title ?? '—';

  const renderStatus = (row: LineRow) => {
    if (!row.status) return '—';
    return (
      <InfiosChip size="compact" {...getStatusChipColor(row.status)}>
        {getStatusLabel(row.status, t)}
      </InfiosChip>
    );
  };

  const renderExpectedDelivery = (row: LineRow) => (
    <FormattedDateTime iso={resolveDeliveryDate(row, shipments)} />
  );

  return (
    <InfiosContainer type="card">
      <div slot="header">
        {orderRefId && data.orderNumber ? (
          <InfiosTextBlock
            onClick={() =>
              onReference?.({ type: 'order', id: orderRefId, action: 'open', label: data.orderNumber })
            }
            weight="bold"
            size="comfortable"
          >
            {t('ChatbotUI.OrderBlocks.OrderNumberLink', { orderNumber: data.orderNumber })}
          </InfiosTextBlock>
        ) : data.orderNumber ? (
          <InfiosTextBlock weight="bold" size="comfortable">
            {t('ChatbotUI.OrderBlocks.OrderNumberLink', { orderNumber: data.orderNumber })}
          </InfiosTextBlock>
        ) : null}
      </div>
      <div slot="header-trailing">
        <InfiosChip size="compact" {...getStatusChipColor(data.status)}>
          {getStatusLabel(data.status, t)}
        </InfiosChip>
      </div>
      <div className={styles.grid}>
        {shipTo ? (
          <InfiosTextBlock
            size="standard"
            pretext={t('ChatbotUI.OrderBlocks.ShipTo')}
          >
            {shipTo}
          </InfiosTextBlock>

        ) : null}
        {data.channel ? (
          <InfiosTextBlock size="standard" pretext={t('ChatbotUI.OrderBlocks.Channel')}>
            {data.channel}
          </InfiosTextBlock>
        ) : null}
        {data.orderDate ? (
          <InfiosTextBlock size="standard" pretext={t('ChatbotUI.OrderBlocks.OrderDate')}>
            <FormattedDateTime iso={data.orderDate} />
          </InfiosTextBlock>
        ) : null}

        {typeof orderTotal === 'number' ? (
          <InfiosTextBlock size="standard" pretext={t('ChatbotUI.OrderBlocks.OrderTotal')}>
            {data.currency ? (
              <InfiosFormatNumber value={orderTotal} type="currency" currency={data.currency} />
            ) : (
              <InfiosFormatNumber value={orderTotal} type="decimal" minimumFractionDigits={2} maximumFractionDigits={2} />
            )}
          </InfiosTextBlock>
        ) : null}
      </div>
      <br />
      {lineRows.length > 0 ? (

        <InfiosTable dataSource={lineRows}>
          <InfiosTableColumn columnId="item">
            <InfiosTableHeaderCell>{t('ChatbotUI.OrderBlocks.Item')}</InfiosTableHeaderCell>
            <InfiosTableCell renderer={renderItem} />
          </InfiosTableColumn>
          <InfiosTableColumn columnId="quantity">
            <InfiosTableHeaderCell>{t('ChatbotUI.OrderBlocks.Quantity')}</InfiosTableHeaderCell>
            <InfiosTableCell>{'{{row.quantity}}'}</InfiosTableCell>
          </InfiosTableColumn>
          <InfiosTableColumn columnId="status">
            <InfiosTableHeaderCell>{t('ChatbotUI.OrderBlocks.Status')}</InfiosTableHeaderCell>
            <InfiosTableCell renderer={renderStatus} />
          </InfiosTableColumn>
          <InfiosTableColumn columnId="expectedDelivery">
            <InfiosTableHeaderCell>{t('ChatbotUI.OrderBlocks.ExpectedDelivery')}</InfiosTableHeaderCell>
            <InfiosTableCell renderer={renderExpectedDelivery} />
          </InfiosTableColumn>
        </InfiosTable>
      ) : null}
      {data.lineItemsTruncated ? <p className={styles.row}>{t('ChatbotUI.OrderBlocks.LineItemsTruncated')}</p> : null}
    </InfiosContainer>
  );
}
